// ---------------------------------------------------------------------------
// แพ็กเกจการใช้งาน (Subscription plans)
// ---------------------------------------------------------------------------
// ร้านที่ยังไม่ซื้อ / แพ็กเกจหมดอายุ ใช้ชุดฟรี (FREE_TIER) ได้ — เมนูอื่นยังเห็นแต่ล็อกไว้ชวนซื้อ
// แต่ละแพ็กเกจกำหนด 2 อย่าง
//   1. modules  = โมดูลที่เปิดใช้ได้ (นอกเหนือจากนี้ถูกล็อก แม้เปิดใน Module Manager)
//   2. limits   = โควตา สาขา / พนักงาน / ลูกค้า / ที่เก็บไฟล์ (GB)  (null = ไม่จำกัด)
//
// ราคาเป็นบาท ยังไม่รวม VAT — รอบรายปีจ่าย 10 เดือน (ฟรี 2 เดือน)
// ---------------------------------------------------------------------------
import { FEATURE_MODULES } from "./feature-modules";
import { addonModules, addonQuota } from "./addons";

export const BRAND = {
  key: "levelup",
  name: "LevelUpCRM",
};

const ALL_MODULES = FEATURE_MODULES.map((item) => item.key);

// โมดูลของแต่ละชั้น (สะสมขึ้นไป)
// Starter = ชุดเดิมของ Starter + Business รวมกัน + AI ชุดเริ่มต้น
const AI_STARTER = ["ai_data_entry", "ai_lead_routing", "ai_followup"];
// Enterprise ไม่รวม — etax / online_payment / white_label ซื้อเสริมได้ · AI ชุด Ultimate ต้องขอใบเสนอราคา Ultimate
const ULTIMATE_ONLY = ["etax", "online_payment", "white_label", "ai_custom_model", "privacy_center", "legacy_integration", "private_cloud"];
const STARTER_MODULES = [
  "crm", "catalog", "billing", "booking", "slip", "chat",
  "resource", "membership", "campaign", "pos", "oms", "inventory", "course", "commission",
  ...AI_STARTER,
];
// Enterprise = ทุกโมดูลยกเว้นชุด Ultimate (รวม AI ชุด Enterprise: ai_churn / ai_sentiment / ai_sales_agent / ai_whatif)
const ENTERPRISE_MODULES = ALL_MODULES.filter((key) => !ULTIMATE_ONLY.includes(key));

export const PLAN_CYCLES = [
  { key: "monthly", months: 1, name: "รายเดือน", nameEn: "Monthly", note: "", noteEn: "" },
  { key: "yearly", months: 12, name: "รายปี", nameEn: "Yearly", note: "จ่าย 10 เดือน ใช้ 12 เดือน", noteEn: "Pay 10 months, get 12" },
];

export const CYCLE_MAP = PLAN_CYCLES.reduce((acc, item) => {
  acc[item.key] = item;
  return acc;
}, {});

/**
 * ราคาต่อ User: ราคา/เดือน = platformFee + seatPrice × จำนวน User (minSeats - maxSeats)
 * จำนวน User = โควตาพนักงานของร้าน · quoteOnly = ซื้อผ่านหน้าเว็บไม่ได้ ต้องขอใบเสนอราคา
 */
export const PLANS = [
  {
    key: "starter",
    tone: "pink",
    name: "AI Sales Starter",
    nameEn: "AI Sales Starter",
    perUser: true,
    seatPrice: 2500,
    platformFee: 0,
    minSeats: 5,
    maxSeats: 15,
    detail: "AI-Powered Sales & Automation",
    detailEn: "AI-Powered Sales & Automation",
    description: "เหมาะสำหรับ SME / ทีมขายขนาดเล็ก-กลาง (5 - 15 User) ที่ต้องการลดงานเอกสารและปิดการขายได้เร็วขึ้น",
    descriptionEn: "For SMEs and small-to-mid sales teams (5 - 15 users) who want less paperwork and faster closing.",
    modules: STARTER_MODULES,
    limits: { branches: 5, staff: null, customers: 50000, storage: 50 },
    highlights: [
      { th: "AI สรุปแชท/สายโทร อัปเดต Pipeline ให้อัตโนมัติ (Zero-Click)", en: "AI zero-click data entry into your pipeline" },
      { th: "แชทรวม LINE OA / Facebook / IG ในหน้าจอเดียว", en: "Omnichannel chat: LINE OA / Facebook / IG" },
      { th: "AI ให้คะแนนลีดและส่งต่อให้เซลล์ที่เหมาะที่สุด", en: "AI lead scoring & routing" },
      { th: "AI ติดตามลูกค้าอัตโนมัติผ่าน LINE / อีเมล", en: "AI auto follow-up via LINE / email" },
      { th: "CRM, Sales Pipeline, แคมเปญ, POS, สต็อก, สมาชิก", en: "CRM, pipeline, campaigns, POS, inventory, membership" },
      { th: "Support ภายใน 8 ชม. (จันทร์-ศุกร์)", en: "Support within 8 hrs (Mon-Fri)" },
    ],
  },
  {
    key: "enterprise",
    tone: "lav",
    featured: true,
    name: "Predictive Enterprise",
    nameEn: "Predictive Enterprise",
    perUser: true,
    seatPrice: 3500,
    platformFee: 50000,
    minSeats: 15,
    maxSeats: 500,
    detail: "Predictive & Autonomous CRM",
    detailEn: "Predictive & Autonomous CRM",
    description: "เหมาะสำหรับธุรกิจขนาดกลาง-ใหญ่ (15 - 50+ User) ที่ต้องการวิเคราะห์เชิงลึกและอัตโนมัติตลอด Customer Journey",
    descriptionEn: "For mid-to-large businesses (15 - 50+ users) needing deep analytics and automation across the customer journey.",
    modules: ENTERPRISE_MODULES,
    limits: { branches: null, staff: null, customers: null, storage: 200 },
    highlights: [
      { th: "ทุกฟีเจอร์ใน AI Sales Starter", en: "Everything in AI Sales Starter" },
      { th: "AI ทำนายลูกค้าที่จะเลิกใช้ & โอกาสขายเพิ่ม พร้อม Action Plan", en: "AI churn & upsell prediction with action plans" },
      { th: "AI วิเคราะห์อารมณ์ลูกค้าเรียลไทม์ เตือนหัวหน้าทีมทันที", en: "Real-time sentiment alerts to supervisors" },
      { th: "AI Sales Agent ตอบคำถามและสร้างใบเสนอราคาเฉพาะบุคคล", en: "AI sales agent with personalised proposals" },
      { th: "วิเคราะห์ขั้นสูง & จำลองสถานการณ์ (What-if)", en: "Advanced analytics & what-if scenarios" },
      { th: "ผ่อน/สัญญา/งานช่าง/ทรัพย์สิน, Export & API", en: "Installments, contracts, field service, assets, export & API" },
      { th: "SLA 99.9% · Support ภายใน 4 ชม.", en: "99.9% SLA · support within 4 hrs" },
    ],
  },
  {
    key: "ultimate",
    tone: "gold",
    name: "Ultimate / Tailored",
    nameEn: "Ultimate / Tailored",
    quoteOnly: true,
    detail: "Full Ecosystem & Privacy-First CRM",
    detailEn: "Full Ecosystem & Privacy-First CRM",
    description: "เหมาะสำหรับองค์กรขนาดใหญ่ สถาบันการเงิน หรือธุรกิจที่ระบบซับซ้อนและเน้นความปลอดภัยสูง",
    descriptionEn: "For large enterprises, financial institutions and complex, security-focused businesses.",
    modules: ALL_MODULES,
    limits: { branches: null, staff: null, customers: null, storage: null },
    highlights: [
      { th: "ทุกฟีเจอร์ใน Predictive Enterprise", en: "Everything in Predictive Enterprise" },
      { th: "เทรน AI โมเดลเฉพาะธุรกิจ", en: "Custom AI model training" },
      { th: "จัดการ Consent & PDPA/GDPR ทุก Touchpoint", en: "Consent & PDPA/GDPR management" },
      { th: "เชื่อมต่อ ERP / POS / Data Warehouse / ระบบเดิม", en: "ERP / POS / data warehouse / legacy integration" },
      { th: "ติดตั้ง Private Cloud / On-Premise", en: "Private cloud / on-premise deployment" },
      { th: "e-Tax, รับชำระออนไลน์, White-label", en: "e-Tax, online payment, white-label" },
      { th: "ราคาตามขอบเขตงาน + ค่าบำรุงรักษารายปี", en: "Priced by scope + annual maintenance" },
    ],
  },
];

/**
 * ชุดฟรี — ร้านใหม่ที่ยังไม่ซื้อ และร้านที่แพ็กเกจหมดอายุ (ข้อมูลยังอยู่ครบ)
 * ใช้ได้เฉพาะโมดูลแกนหลัก โควตาเล็ก ๆ พอลองใช้ · ไม่ได้ขาย ไม่อยู่ใน PLANS
 * โควตาลูกค้าต้องตรงกับ FREE_CUSTOMER_LIMIT ใน gs/Code.gs (ด่านสมัครผ่านลิงก์ร้าน)
 */
export const FREE_TIER = {
  modules: ["crm", "catalog", "billing"],
  limits: { branches: 1, staff: 1, customers: 5, storage: 0.5 },
};

export const PLAN_MAP = PLANS.reduce((acc, item) => {
  acc[item.key] = item;
  return acc;
}, {});

// แพ็กเกจเดิมที่เลิกขาย -> ย้ายไปแพ็กเกจใหม่อัตโนมัติ (ตอนอ่าน ไม่ได้แก้ชีต · ต่ออายุครั้งหน้าจะบันทึกเป็นรหัสใหม่)
export const LEGACY_PLAN_KEYS = { business: "starter" };
// จำนวน User ของร้านที่ซื้อก่อนคิดราคาต่อ User (ยังไม่มี planSeats) = โควตาพนักงานเดิม · null = ไม่จำกัด
const LEGACY_SEATS = { starter: 10, business: 50, enterprise: null, ultimate: null };

export const getPlan = (key) => PLAN_MAP[LEGACY_PLAN_KEYS[key] || key] || null;

/** จำนวน User ที่ซื้อได้ของแพ็กเกจ (ปัดเข้าช่วง min-max) */
export const clampSeats = (planKey, seats) => {
  const plan = getPlan(planKey);
  if (!plan?.perUser) return 0;
  const value = Math.floor(Number(seats)) || plan.minSeats;
  return Math.min(plan.maxSeats, Math.max(plan.minSeats, value));
};

/** ราคาแพ็กเกจตามรอบบิลและจำนวน User · quoteOnly = 0 (ซื้อผ่านหน้าเว็บไม่ได้) */
export const priceOf = (planKey, cycle = "monthly", seats) => {
  const plan = getPlan(planKey);
  if (!plan || plan.quoteOnly) return 0;
  const monthly = plan.perUser ? plan.platformFee + plan.seatPrice * clampSeats(planKey, seats) : plan.price;
  return cycle === "yearly" ? monthly * 10 : monthly;
};

/** ราคาเริ่มต้น/เดือน (จำนวน User ขั้นต่ำ) ไว้โชว์ในหน้าขาย · quoteOnly = null */
export const startingPrice = (planKey) => {
  const plan = getPlan(planKey);
  if (!plan || plan.quoteOnly) return null;
  return priceOf(plan.key, "monthly", plan.minSeats);
};

/** จำนวน User ของร้าน — null = ไม่จำกัด */
export const seatsOf = (organization) => {
  const plan = getPlan(organization?.plan);
  if (!plan?.perUser) return null;
  const saved = Number(organization?.planSeats);
  if (saved > 0) return saved;
  const legacy = LEGACY_SEATS[organization?.plan];
  return legacy === undefined ? plan.minSeats : legacy;
};

export const monthsOf = (cycle) => CYCLE_MAP[cycle]?.months || 1;

/** โมดูลที่แพ็กเกจนี้เปิดให้ใช้ (ไม่มีแพ็กเกจ = ใช้อะไรไม่ได้เลย) */
export const planModules = (planKey) => getPlan(planKey)?.modules || [];

export const planIncludes = (planKey, moduleKey) => !moduleKey || planModules(planKey).includes(moduleKey);

/** โควตาของแพ็กเกจ — null = ไม่จำกัด */
export const planLimit = (planKey, name) => {
  const plan = getPlan(planKey);
  if (!plan) return 0;
  const value = plan.limits[name];
  return value === undefined ? null : value;
};

/** แพ็กเกจต่ำสุดที่มีโมดูลนี้ (ใช้ชวนอัปเกรดในหน้าที่ถูกล็อก) */
export const lowestPlanWith = (moduleKey) => PLANS.find((plan) => plan.modules.includes(moduleKey)) || null;

const dayDiff = (from, to) => Math.ceil((new Date(to).getTime() - new Date(from).getTime()) / 86400000);

/**
 * สถานะแพ็กเกจของร้าน — ยึดวันหมดอายุจริงเสมอ ไม่เชื่อ field status ที่ค้างในชีต
 * status: none (ยังไม่เคยซื้อ) | active | expired
 */
export const resolveSubscription = (organization) => {
  const plan = getPlan(organization?.plan);
  const planKey = plan ? plan.key : "";
  const expireAt = organization?.planExpireAt || "";

  if (!plan || !expireAt) {
    return {
      planKey: "", plan: null, seats: null, cycle: "", status: "none", expireAt: "", daysLeft: 0, isActive: false, isFree: true,
      modules: [...FREE_TIER.modules], addonModules: [], addonQuota: { branches: 0, staff: 0, customers: 0, storage: 0 },
    };
  }

  const daysLeft = dayDiff(new Date(), expireAt);
  const isActive = daysLeft > 0;
  // หมดอายุ = ตกกลับมาใช้ชุดฟรี (ยังเก็บ plan เดิมไว้โชว์ในหน้าแพ็กเกจ/ปุ่มต่ออายุ)
  if (!isActive) {
    return {
      planKey, plan, seats: seatsOf(organization), cycle: organization?.planCycle || "monthly", status: "expired",
      expireAt, daysLeft: 0, isActive: false, isFree: true,
      modules: [...FREE_TIER.modules], addonModules: [], addonQuota: { branches: 0, staff: 0, customers: 0, storage: 0 },
    };
  }
  const extraModules = addonModules(organization).filter((key) => !plan.modules.includes(key));

  return {
    planKey,
    plan,
    seats: seatsOf(organization),
    cycle: organization?.planCycle || "monthly",
    status: "active",
    expireAt,
    daysLeft,
    isActive: true,
    isFree: false,
    modules: [...plan.modules, ...extraModules],
    addonModules: extraModules,
    addonQuota: addonQuota(organization),
  };
};

/** โควตารวมแพ็คเสริม — แพ็กเกจไม่จำกัดก็ยังไม่จำกัด (null) */
export const effectiveLimit = (subscription, name) => {
  if (subscription?.isFree) return FREE_TIER.limits[name] ?? 0;
  // แพ็กเกจต่อ User: โควตาพนักงาน = จำนวน User ที่ซื้อ
  const base = name === "staff" && subscription?.plan?.perUser ? subscription.seats ?? null : planLimit(subscription?.planKey, name);
  if (base === null) return null;
  return base + (Number(subscription?.addonQuota?.[name]) || 0);
};

/** วันหมดอายุใหม่หลังจ่ายเงิน — ถ้ายังไม่หมดอายุให้ต่อจากของเดิม */
export const nextExpireAt = (currentExpireAt, cycle) => {
  const base = currentExpireAt && new Date(currentExpireAt) > new Date() ? new Date(currentExpireAt) : new Date();
  base.setMonth(base.getMonth() + monthsOf(cycle));
  return base.toISOString();
};
